import { createContext, useContext, useState, ReactNode } from 'react';

interface Solution {
  id: string;
  name: string;
  logo_url: string | null;
  ai_provider: string | null;
}

interface CompareContextType {
  selectedSolutions: Solution[];
  addSolution: (solution: Solution) => void;
  removeSolution: (id: string) => void;
  clearSolutions: () => void;
  isSelected: (id: string) => boolean;
  canAddMore: boolean;
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}

const CompareContext = createContext<CompareContextType | undefined>(undefined);

const MAX_COMPARE = 5;

export function CompareProvider({ children }: { children: ReactNode }) {
  const [selectedSolutions, setSelectedSolutions] = useState<Solution[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const addSolution = (solution: Solution) => {
    if (selectedSolutions.length < MAX_COMPARE && !selectedSolutions.find(s => s.id === solution.id)) {
      setSelectedSolutions(prev => [...prev, solution]);
    }
  };

  const removeSolution = (id: string) => {
    setSelectedSolutions(prev => prev.filter(s => s.id !== id));
  };

  const clearSolutions = () => {
    setSelectedSolutions([]);
    setIsModalOpen(false);
  };

  const isSelected = (id: string) => selectedSolutions.some(s => s.id === id);

  const canAddMore = selectedSolutions.length < MAX_COMPARE;

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <CompareContext.Provider value={{
      selectedSolutions,
      addSolution,
      removeSolution,
      clearSolutions,
      isSelected,
      canAddMore,
      isModalOpen,
      openModal,
      closeModal
    }}>
      {children}
    </CompareContext.Provider>
  );
}

export function useCompare() {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error('useCompare must be used within a CompareProvider');
  }
  return context;
}
